/**
 * Floating Toolbar
 * Draggable toolbar with vector editing tools similar to Figma/Illustrator
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  MousePointer2,
  Type,
  Shapes,
  Minus,
  ImageIcon,
  Video,
  Volume2,
  FolderOpen,
  Pipette,
  Hand,
  GripVertical,
  GripHorizontal,
  LayoutGrid,
} from 'lucide-react';
import { useToolbarStore } from '@/stores/toolbarStore';
import type { ToolId, Tool } from '@/types/tools';
import { ToolSettings } from './ToolSettings';
import { triggerImageUpload, triggerVideoUpload, triggerAudioUpload } from '@/lib/mediaUpload';

// Tool definitions with icons
const TOOLS: Tool[] = [
  { id: 'select', label: 'Select', icon: MousePointer2, cursor: 'default', shortcut: 'V' },
  { id: 'text', label: 'Text', icon: Type, cursor: 'text', shortcut: 'T', hasSettings: true },
  { id: 'shape', label: 'Shape', icon: Shapes, cursor: 'crosshair', shortcut: 'S', hasSettings: true },
  { id: 'line', label: 'Line', icon: Minus, cursor: 'crosshair', shortcut: 'L', hasSettings: true },
  { id: 'image', label: 'Image', icon: ImageIcon, cursor: 'default', shortcut: 'I' },
  { id: 'video', label: 'Video', icon: Video, cursor: 'default', shortcut: 'U' },
  { id: 'audio', label: 'Audio', icon: Volume2, cursor: 'default', shortcut: 'A' },
  { id: 'resource', label: 'Resources', icon: FolderOpen, cursor: 'default', shortcut: 'R', hasSettings: true },
  { id: 'colorPicker', label: 'Color Picker', icon: Pipette, cursor: 'crosshair', shortcut: 'P' },
  { id: 'hand', label: 'Pan', icon: Hand, cursor: 'grab', shortcut: 'H' },
];

export { TOOLS };

interface ToolButtonProps {
  tool: Tool;
  isActive: boolean;
  onClick: () => void;
}

function ToolButton({ tool, isActive, onClick }: ToolButtonProps) {
  const Icon = tool.icon;

  return (
    <button
      onClick={onClick}
      className={`
        relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors
        ${isActive
          ? 'bg-primary/20 text-primary'
          : 'text-fg-muted hover:bg-white/10 hover:text-fg'
        }
      `}
      title={`${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ''}`}
    >
      <Icon size={18} strokeWidth={1.5} />
    </button>
  );
}

export function FloatingToolbar() {
  const {
    activeTool,
    orientation,
    position,
    setActiveTool,
    toggleOrientation,
    setPosition,
  } = useToolbarStore();

  const toolbarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Get the active tool config
  const activeToolConfig = TOOLS.find(t => t.id === activeTool);

  // Handle tool selection with special cases for image/video/audio
  const handleToolSelect = useCallback((toolId: ToolId) => {
    if (toolId === 'image') {
      // Directly trigger image upload dialog
      triggerImageUpload();
    } else if (toolId === 'video') {
      // Directly trigger video upload dialog
      triggerVideoUpload();
    } else if (toolId === 'audio') {
      // Directly trigger audio upload dialog
      triggerAudioUpload();
    } else {
      setActiveTool(toolId);
    }
  }, [setActiveTool]);

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!toolbarRef.current) return;

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  }, [position]);

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: Math.max(0, e.clientX - dragOffset.x),
        y: Math.max(0, e.clientY - dragOffset.y),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, setPosition]);

  // Keyboard shortcuts for tools
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Don't handle if modifier keys are pressed (except for tool shortcuts)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toUpperCase();

      // Find tool by shortcut
      const tool = TOOLS.find(t => t.shortcut === key);
      if (tool) {
        e.preventDefault();
        setActiveTool(tool.id);
      }

      // Escape returns to select
      if (e.key === 'Escape') {
        setActiveTool('select');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setActiveTool]);

  const isVertical = orientation === 'vertical';
  const GripIcon = isVertical ? GripVertical : GripHorizontal;

  return (
    <div
      ref={toolbarRef}
      className="absolute z-50 select-none"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {/* Main toolbar */}
      <div
        className={`
          bg-navy-light/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl
          ${isVertical ? 'flex flex-col' : 'flex flex-row'}
        `}
      >
        {/* Drag handle + orientation toggle */}
        <div
          className={`
            flex items-center justify-center gap-0.5 cursor-move
            ${isVertical ? 'px-2 py-1.5 border-b border-white/10' : 'py-2 px-1.5 border-r border-white/10'}
            ${isVertical ? 'flex-row' : 'flex-col'}
          `}
          onMouseDown={handleDragStart}
        >
          <GripIcon size={14} className="text-fg-subtle" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleOrientation();
            }}
            className="p-1 text-fg-subtle hover:text-fg hover:bg-white/10 rounded transition-colors"
            title={`Switch to ${isVertical ? 'horizontal' : 'vertical'} layout`}
          >
            <LayoutGrid size={12} />
          </button>
        </div>

        {/* Tool buttons */}
        <div
          className={`
            p-1 gap-0.5
            ${isVertical ? 'flex flex-col' : 'flex flex-row'}
          `}
        >
          {TOOLS.map((tool) => (
            <ToolButton
              key={tool.id}
              tool={tool}
              isActive={activeTool === tool.id}
              onClick={() => handleToolSelect(tool.id)}
            />
          ))}
        </div>
      </div>

      {/* Tool settings panel (anchored below toolbar) */}
      {activeToolConfig?.hasSettings && (
        <ToolSettings toolId={activeTool} orientation={orientation} />
      )}
    </div>
  );
}
